import { AfterViewInit, Component, HostBinding, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ECampaignFormSection } from '@campaign-form/campaign-form.interface';
import { ECampaignStatus } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  imports: [
    MatExpansionModule,
    IconComponent,
    TranslateModule,
    MatButtonModule,
    DotsSpinnerComponent,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
  ],
  selector: 'app-campaign-form-expansion-panel',
  styleUrl: './campaign-form-expansion-panel.component.scss',
  templateUrl: './campaign-form-expansion-panel.component.html',
})
export class CampaignFormExpansionPanelComponent implements AfterViewInit {
  public readonly section = input.required<ECampaignFormSection>();
  public readonly changed = input.required<boolean>();
  public readonly campaignStatus = input.required<ECampaignStatus | null>();

  public readonly expanded = input<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly sectionCompleted = input<boolean>(false);
  public readonly submitting = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly campaignWithCustomerLoading = input<boolean>(false);

  public readonly canSave = input<boolean>(true);
  public readonly canSaveDisabledMessage = input<string | null>(null);

  public readonly sectionManage = output<void>();
  public readonly sectionClose = output<MatExpansionPanel>();
  public readonly sectionSave = output<MatExpansionPanel>();

  public readonly ECampaignFormSection = ECampaignFormSection;

  @HostBinding('class.transitions-enabled')
  public transitionsEnabled = false;

  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  public ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.transitionsEnabled = true;
    });
  }

  public onSectionManageClick(event: Event): void {
    event?.preventDefault();

    this.sectionManage.emit();
  }

  public onSectionCloseClick(event: Event, expansionPanel: MatExpansionPanel): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.changed() && this.campaignStatus() !== ECampaignStatus.ended) {
      this.openSectionConfirmDialog(expansionPanel);

      return;
    }

    this.sectionClose.emit(expansionPanel);
  }

  public onSectionSaveClick(event: Event, expansionPanel: MatExpansionPanel): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.campaignWithCustomerLoading()) {
      return;
    }

    this.sectionSave.emit(expansionPanel);
  }

  private openSectionConfirmDialog(expansionPanel: MatExpansionPanel): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.campaignForm.sectionConfirmDialog.continueEditing'),
        confirmText: this.translate.instant('pages.campaignForm.sectionConfirmDialog.discard'),
        message: this.translate.instant('pages.campaignForm.sectionConfirmDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.campaignForm.sectionConfirmDialog.title'),
      },
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sectionClose.emit(expansionPanel);
      }
    });
  }
}
